import { addDays, startOfDay } from "date-fns";
import type { Recording } from "../../api/types";
import { parseUtcDate } from "../../utils/format";

export type RecordingInterval = { recording: Recording; start: number; end: number };

export const localDay = (time: number) => startOfDay(time).getTime();

export const nextLocalDay = (time: number, days = 1) => addDays(time, days).getTime();

export function recordingIntervals(
  recordings: Recording[],
  from?: string,
  to?: string,
  includeArchived = false,
) {
  const lower = from ? new Date(from).getTime() : -Infinity;
  const upper = to ? new Date(to).getTime() : Infinity;
  const seen = new Set<string>();
  return recordings.flatMap(recording => {
    const start = parseUtcDate(recording.date_started).getTime();
    const end = parseUtcDate(recording.date_ended).getTime();
    // Match the overview/list's session-start date scope, including unanalyzed sessions.
    if (
      (!includeArchived && !recording.include_in_processes) ||
      seen.has(recording.id) ||
      !Number.isFinite(start) ||
      !Number.isFinite(end) ||
      end <= start ||
      start < lower ||
      start > upper
    )
      return [];
    seen.add(recording.id);
    return [{ recording, start, end }];
  });
}

export function recordingDays(intervals: RecordingInterval[], from?: string, to?: string) {
  let first = Infinity;
  let last = -Infinity;
  for (const interval of intervals) {
    first = Math.min(first, interval.start);
    last = Math.max(last, interval.end - 1);
  }
  const start = localDay(
    from
      ? new Date(from).getTime()
      : Number.isFinite(first)
        ? first
        : to
          ? new Date(to).getTime()
          : Date.now(),
  );
  const end = localDay(to ? new Date(to).getTime() : Number.isFinite(last) ? last : start);
  const days: number[] = [];
  for (let day = start; day <= end; day = nextLocalDay(day)) days.push(day);
  return days;
}

/** Coverage, not summed device time: overlapping recordings count only once. */
export function recordedSeconds(intervals: RecordingInterval[], from: number, to: number) {
  const spans = intervals
    .map(interval => ({ start: Math.max(from, interval.start), end: Math.min(to, interval.end) }))
    .filter(span => span.end > span.start)
    .sort((a, b) => a.start - b.start);
  let end = from;
  let milliseconds = 0;
  for (const span of spans) {
    milliseconds += Math.max(0, span.end - Math.max(end, span.start));
    end = Math.max(end, span.end);
  }
  return milliseconds / 1000;
}
